"use client";
import { Button, Input } from "@nextui-org/react";
import { signInWithForm } from "@repo/web/lib/auth/action";
import { fetchPost } from "@repo/web/lib/utils";
import { useTranslations } from "next-intl";
import {
	useActionState,
	useEffect,
	useMemo,
	useState,
	useTransition,
} from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "@repo/i18n";

interface SignInForm {
	username: string;
	code: string;
}

export const SignInForms = ({ entry }: { entry?: string }) => {
	const t = useTranslations("SignInPage");
	const home = useTranslations("HomePage");
	const {
		register,
		handleSubmit,
		formState: { errors },
		getValues,
		watch,
	} = useForm<SignInForm>();
	const [sendCodeSuccess, setSendCodeSuccess] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [count, setCount] = useState(60);
	const watchUsername = watch("username");
	const watchCode = watch("code");
	const isDisabled = useMemo(
		() => !(watchUsername && watchCode),
		[watchUsername, watchCode]
	);
	const [error, setError] = useState<string | null>(null);
	const [state, submit, isPending] = useActionState(signInWithForm, {
		message: "",
	});
	const router = useRouter();
	const [isTransition, startTransition] = useTransition();

	function onSubmit(data: SignInForm) {
		return startTransition(async () => {
			const callbackUrl = "/providers";
			submit({ ...data });
			router.replace(callbackUrl);
		});
	}

	async function sendCode() {
		// 获取表单数据当中的手机号
		const username = getValues("username");
		if (!username) {
			setError(t("form-error-tips"));
			return;
		}
		setIsSending(true);
		// 发送验证码数据到后台
		fetchPost("/api/captcha", { username })
			.then(() => {
				setSendCodeSuccess(() => true);
				setError(t("code-sent-tips"));
			})
			.catch((error) => {
				console.log("error", error);
				setError(error);
			})
			.finally(() => {
				setIsSending(false);
			});
	}

	// 当验证码发送成功后，按钮倒计时60秒
	useEffect(() => {
		let timer: any;
		if (sendCodeSuccess && count) {
			timer = setInterval(() => {
				setCount((prevCount) => prevCount - 1);
			}, 1000);
		}
		// 清除定时器
		return () => clearInterval(timer);
	}, [sendCodeSuccess, count]);
	return (
		<div className="w-full relative z-20 md:w-[540px] md:-mt-40 p-4 text-gray-600">
			<div className="flex items-center border-0 border-b text-2xl font-bold  border-solid border-primary px-2 pb-2">
				{home("title")}
			</div>
			<form onSubmit={handleSubmit(onSubmit)} className="mb-4 pt-12">
				<div className="space-y-10 mb-5">
					<div>
						<Input
							{...register("username", {
								required: true,
								pattern: {
									// 验证手机号或邮箱格式
									value:
										/^(1[3-9]\d{9}|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/,
									message: t("username-error"),
								},
							})}
							label={t("username")}
							placeholder={t("username-placeholder")}
							size="lg"
							labelPlacement="outside"
							radius="full"
							classNames={{
								label: "text-black/70 px-3",
								input: [
									"bg-transparent",
									"text-black/90",
									"placeholder:text-default-700/50",
								],
								innerWrapper: "bg-transparent",
								inputWrapper: [
									"bg-white",
									"backdrop-blur-xl",
									"backdrop-saturate-200",
									"hover:bg-white",
									"group-pdf_data-[focused=true]:bg-white",
									"!cursor-text",
								],
							}}
							isInvalid={!!errors.username}
							errorMessage={errors.username?.message ?? ""}
						/>
					</div>
					<div>
						<Input
							{...register("code", {
								required: true,
								pattern: {
									value: /.{4,}/,
									message: t("code-error"),
								},
							})}
							label={t("code")}
							size="lg"
							autoComplete="off"
							type="number"
							maxLength={4}
							labelPlacement="outside"
							radius="full"
							classNames={{
								label: "text-black/70 px-3",
								input: [
									"bg-transparent",
									"text-black/90",
									"placeholder:text-default-700/50",
								],
								innerWrapper: "bg-transparent",
								inputWrapper: [
									"bg-white",
									"backdrop-blur-xl",
									"backdrop-saturate-200",
									"hover:bg-white",
									"group-pdf_data-[focused=true]:bg-white",
									"!cursor-text",
								],
							}}
							isInvalid={!!errors.code}
							errorMessage={errors.code?.message ?? ""}
							endContent={
								sendCodeSuccess && count > 0 ? (
									<Button
										size="sm"
										disabled
										color="primary"
										className="text-xs"
									>
										{t("btn-later", { count })}
									</Button>
								) : (
									<Button
										isLoading={isSending}
										size="sm"
										color="primary"
										className="text-xs"
										onClick={sendCode}
									>
										{t("btn-send")}
									</Button>
								)
							}
						/>
					</div>
				</div>

				{(error || state?.message) && (
					<div className="text-sm text-red-500 text-center py-3">
						{error || state?.message}
					</div>
				)}
				<Button
					type="submit"
					isLoading={isPending || isTransition}
					isDisabled={isDisabled}
					className="w-full px-4 h-12 text-white font-medium bg-[#003248] hover:bg-[#003248]/80 active:bg-[#003248] rounded-full duration-150"
				>
					{t("btn-login")}
				</Button>
			</form>
			<div className="text-center">{t("no-account-tips")}</div>
		</div>
	);
};
